import { motion } from "motion/react";
import { CalendarCheck, Clock, Phone, ShieldCheck, Sparkles, MousePointerClick } from "lucide-react";
import { Button, BookButton, Eyebrow } from "../components/Buttons";
import { Stagger, Reveal } from "../components/Reveal";
import Hours from "../sections/Hours";
import { fadeUp, EASE } from "../motion";
import { BOOKING_URL, PHONE, PHONE_HREF, SERVICES } from "../config";
import usePageMeta from "../usePageMeta";

const item = {
  hidden: { opacity: 0, y: 28, filter: "blur(6px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.85, ease: EASE } },
};

const STEPS = [
  { icon: MousePointerClick, t: "Choose your service", d: "Gel, acrylics, manicure or a spa pedicure — pick what you'd love today." },
  { icon: CalendarCheck, t: "Pick a time", d: "See live availability and choose the day and time that suits you." },
  { icon: Sparkles, t: "Relax & enjoy", d: "You'll get a confirmation straight away. Then just come in and unwind." },
];

export default function Booking() {
  usePageMeta(
    "Book Online — No. 1 Nails & Spa Manly",
    "Book your gel manicure, acrylics or spa pedicure online in seconds at No. 1 Nails & Spa, Manly."
  );

  return (
    <main className="booking">
      {/* ---------- Intro ---------- */}
      <section className="bk-hero section--alt">
        <motion.div className="bk-hero__ring" aria-hidden="true"
          animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }} />
        <div className="container bk-hero__inner">
          <motion.div initial="hidden" animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } }}>
            <motion.div variants={item}><Eyebrow>BOOK ONLINE</Eyebrow></motion.div>
            <motion.h1 className="hero__title" variants={item}>
              Your <span className="editorial">moment of calm</span> is one click away
            </motion.h1>
            <motion.p className="hero__sub lead" variants={item}>
              Reserve your appointment online in under a minute. Choose your service, pick a
              time that suits you and we'll have everything ready when you arrive.
            </motion.p>
            <motion.div className="hero__ctas" variants={item}>
              <motion.div className="bk-pulse"
                animate={{ boxShadow: ["0 0 0 0 rgba(224,26,37,0.45)", "0 0 0 16px rgba(224,26,37,0)"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}>
                <BookButton className="btn--lg">Book your appointment</BookButton>
              </motion.div>
              <Button href={PHONE_HREF} variant="blue-outline"><Phone aria-hidden="true" />Call {PHONE}</Button>
            </motion.div>
            <motion.p className="bk-note" variants={item}>
              Opens our secure booking page on Caljn in a new tab.
            </motion.p>
          </motion.div>

          <motion.aside className="bk-card"
            initial={{ opacity: 0, x: 40, rotate: 2 }} animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.45 }}>
            <h3>Good to know</h3>
            <ul>
              <li><Clock aria-hidden="true" /><span>Late night Thursday until 7:30pm</span></li>
              <li><ShieldCheck aria-hidden="true" /><span>100% satisfaction guaranteed</span></li>
              <li><CalendarCheck aria-hidden="true" /><span>Walk-ins welcome — booking guarantees your spot</span></li>
            </ul>
            <p>Need to change or cancel? Just give us a call on <a href={PHONE_HREF}>{PHONE}</a>.</p>
          </motion.aside>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section className="section bk-steps">
        <div className="container">
          <Stagger as="header" className="services__head">
            <motion.div variants={fadeUp}><Eyebrow>HOW IT WORKS</Eyebrow></motion.div>
            <motion.h2 variants={fadeUp}>Three easy steps</motion.h2>
          </Stagger>
          <Stagger className="bk-steps__grid" gap={0.15}>
            {STEPS.map(({ icon: Icon, t, d }, i) => (
              <motion.div key={t} className="bk-step" variants={fadeUp}
                whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}>
                <span className="bk-step__num">0{i + 1}</span>
                <motion.span className="bk-step__icon"
                  initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 380, damping: 16, delay: 0.3 + i * 0.15 }}>
                  <Icon aria-hidden="true" />
                </motion.span>
                <h3 className="svc__t">{t}</h3>
                <p className="svc__d">{d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- Service list ---------- */}
      <section className="section section--alt">
        <div className="container">
          <Stagger as="header" className="services__head">
            <motion.div variants={fadeUp}><Eyebrow>CHOOSE A SERVICE</Eyebrow></motion.div>
            <motion.h2 variants={fadeUp}>What would you like today?</motion.h2>
          </Stagger>
          <Stagger as="ul" className="bk-list" gap={0.07} amount={0.1}>
            {SERVICES.map((s) => (
              <motion.li key={s.t} className="bk-row" variants={fadeUp}
                whileHover={{ x: 6 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}>
                <div>
                  <h3 className="bk-row__t">{s.t}</h3>
                  <p className="bk-row__d">{s.d}</p>
                </div>
                <a className="btn btn--blue-outline btn--sm" href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Book →
                </a>
              </motion.li>
            ))}
          </Stagger>
        </div>
      </section>

      <Hours />

      {/* ---------- Closing CTA ---------- */}
      <section className="bk-cta section--red">
        <Reveal className="container bk-cta__inner">
          <div>
            <h2>Ready to be pampered?</h2>
            <p>Book in seconds — we can't wait to see you in Manly.</p>
          </div>
          <div className="bk-cta__btns">
            <BookButton variant="white">Book now</BookButton>
            <Button href={PHONE_HREF} variant="ghost-white"><Phone aria-hidden="true" />{PHONE}</Button>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
